import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  ServiceTicketType,
  ServiceTicketStatus,
  Note,
  ActionLog,
  TechnicianType,
  ChecklistTemplateType,
  ServiceTicketChecklistType,
  ServiceTicketsStats,
  ServiceOrganizationListItemType,
  ServiceOrganizationSystemType,
} from '../../types/service-calls/service-calls.type';
import { parseApiError, rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { EquipmentGroupDetailsType } from '../../types/equipment-group/equipment-group.type';

const additionalQueryParams = (selectedCompany: any, selectedLocation: any, includeSuborgs: boolean, user: any) => {
  const params: any = {};
  const isTelCloudUser = user?.organization?.level === 0;

  if (selectedCompany && selectedCompany.id !== 'all') {
    params.org_id = selectedCompany.id;
  } else if (!isTelCloudUser && user?.organization?.id) {
    params.org_id = user.organization.id;
  }

  if (selectedLocation && selectedLocation.id !== 'all' && +selectedLocation.id) {
    params.locationId = selectedLocation.id;
  }

  if (includeSuborgs) {
    params.inc_sub = true;
  }

  return params;
};

export const getServiceTicketsList = createAsyncThunk<
  PaginationType<ServiceTicketType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('service-calls/getServiceTicketsList', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().authReducer.user
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    try {
      const response = await axios.get<PaginationType<ServiceTicketType>>(
        `${process.env.REACT_APP_API_URL}/service-ticket${queryString ? `?${queryString}` : ''}`,
        {
          signal: controller?.signal,
        }
      );
      return response.data;
    } catch (apiError) {
      console.error('API Error fetching service tickets:', parseApiError({ error: apiError }));
      return {
        items: [],
        meta: {
          totalItems: 0,
          totalPages: 1,
          currentPage: 1,
        },
      };
    }
  } catch (error) {
    const errorMessage = parseApiError({ error, fallback: 'Error during fetching service tickets!' });
    console.error('Service tickets list error:', errorMessage);
    return thunkAPI.rejectWithValue(errorMessage);
  }
});

export const getServiceTicketsStats = createAsyncThunk<
  ServiceTicketsStats,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('service-calls/getServiceTicketsStats/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().authReducer.user
    );

    const response = await axios.get<ServiceTicketsStats>(`${process.env.REACT_APP_API_URL}/service-ticket/stats`, {
      signal: controller?.signal,
      params: { ...params, ...additionalParams },
    });

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error });
  }
});

const refreshServiceTicketsListAndSummary = (thunkAPI: {
  getState: () => RootReducerState;
  dispatch: (action: unknown) => unknown;
}) => {
  const params = thunkAPI.getState().serviceCallsReducer.serviceTicketsRouteParams;
  thunkAPI.dispatch(getServiceTicketsList(params));
  thunkAPI.dispatch(getServiceTicketsStats(params));
};

// Get service ticket details
export const getServiceTicket = createAsyncThunk<ServiceTicketType, { id: number }, { state: RootReducerState }>(
  'service-calls/getServiceTicket',
  async ({ id }, thunkAPI) => {
    try {
      const selectedCompany = thunkAPI.getState().sharedReducer.selectedCompany;
      const selectedLocation = thunkAPI.getState().sharedReducer.selectedLocation;
      const user = thunkAPI.getState().authReducer.user;

      const params: Record<string, string> = {};

      const isTelCloudUser = user?.organization?.level === 0;

      if (selectedCompany && selectedCompany.id !== 'all') {
        params['org_id'] = `${selectedCompany.id}`;
      } else if (!isTelCloudUser && user?.organization?.id) {
        params['org_id'] = user.organization.id.toString();
      }

      if (selectedLocation && selectedLocation.id !== 'all' && +selectedLocation.id) {
        params['locationId'] = `${selectedLocation.id}`;
      }

      const queryString = Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&');

      const url = `${process.env.REACT_APP_API_URL}/service-ticket/${id}${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);

      return response.data.serviceTicket;
    } catch (error) {
      console.error('Error fetching service ticket details:', error);
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching service ticket details!' });
    }
  }
);

// Create service ticket
export const postServiceTicket = createAsyncThunk<
  ServiceTicketType,
  {
    data: {
      org: { id: number; name: string };
      location: { id: number; name: string };
      user?: {
        userId: number;
        firstName: string;
        lastName: string;
        email?: string;
      };
      requestedDateTimes: Array<{
        date: string;
        timeOptionId: number;
      }>;
      serviceVariant: 'installation' | 'breakfix' | 'custom' | 'site_survey' | 'virtual_site_survey';
      isSiteUnderConstruction: boolean;
      isCutoverInsideWiringNeeded: boolean;
      customService?: { description: string };
      breakfixService?: { description: string };
      siteSurveyService?: { description: string };
      virtualSiteSurveyService?: { description: string };
      installationService?: {
        isNewLine: boolean;
      };
      contacts: Array<{
        firstName: string;
        lastName: string;
        phoneNumber: {
          number: string;
          countryCode: string;
        };
        email: string;
        role: string;
      }>;
    };
    fetchList?: boolean;
  },
  { state: RootReducerState }
>('service-calls/postServiceTicket', async ({ data, fetchList = true }, thunkAPI) => {
  try {
    if (!data.user) {
      const currentUser = thunkAPI.getState().authReducer.user;
      if (currentUser) {
        data.user = {
          userId: currentUser.id,
          firstName: currentUser.firstname,
          lastName: currentUser.lastname,
          email: currentUser.email,
        };
      }
    }

    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-ticket`, data);

    if (fetchList) {
      refreshServiceTicketsListAndSummary(thunkAPI);
    }
    return response.data.serviceTicket;
  } catch (error) {
    console.error('Error creating service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating service ticket!' });
  }
});

// Update service ticket
export const patchServiceTicket = createAsyncThunk<
  ServiceTicketType,
  {
    id: number;
    data: Partial<{
      orgId: number;
      locationId: number;
      requestedDateTimes: Array<{
        date: string;
        timeOptionId: number;
      }>;
      status: ServiceTicketStatus;
      holdReason?: string;
      serviceVariant: 'installation' | 'breakfix' | 'custom' | 'site_survey';
      isSiteUnderConstruction: boolean;
      isCutoverInsideWiringNeeded: boolean;
      customService: { description: string };
      breakfixService: { description: string };
      siteSurveyService: { description: string };
      installationService: {
        isNewLine: boolean;
      };
    }>;
    fetchList?: boolean;
    statusOnly?: boolean;
  },
  { state: RootReducerState }
>('service-calls/patchServiceTicket', async ({ id, data, fetchList = true, statusOnly = false }, thunkAPI) => {
  try {
    let response: { data: ServiceTicketType };

    if (statusOnly && 'status' in data) {
      response = await axios.patch<ServiceTicketType>(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/status`, {
        status: data.status,
        ...(data.holdReason != null && data.holdReason !== '' ? { holdReason: data.holdReason } : {}),
      });
    } else {
      response = await axios.patch<ServiceTicketType>(`${process.env.REACT_APP_API_URL}/service-ticket/${id}`, data);
    }

    if (fetchList) {
      refreshServiceTicketsListAndSummary(thunkAPI);
    }
    return response.data.serviceTicket;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating service ticket!' });
  }
});

export const patchServiceTicketActivateEquipmentGroup = createAsyncThunk<
  EquipmentGroupDetailsType,
  { serviceTicketId: number; equipmentGroupId: number },
  { state: RootReducerState }
>('service-calls/patchServiceTicketActivateEquipmentGroup', async ({ serviceTicketId, equipmentGroupId }, thunkAPI) => {
  try {
    const response = await axios.patch<EquipmentGroupDetailsType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/activate-equipment-group`,
      { equipmentGroupId }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({
      thunkAPI,
      error,
      fallback: 'Could not mark equipment group in service.',
    });
  }
});

export const createLinkedSiteSurveyFromInstallation = createAsyncThunk<
  ServiceTicketType,
  { installationServiceCallId: number },
  { state: RootReducerState }
>('service-calls/createLinkedSiteSurveyFromInstallation', async ({ installationServiceCallId }, thunkAPI) => {
  try {
    const response = await axios.post<{ serviceTicket: ServiceTicketType }>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${installationServiceCallId}/linked-site-survey`
    );
    refreshServiceTicketsListAndSummary(thunkAPI);
    return response.data.serviceTicket;
  } catch (error) {
    return rejectWithApiError({
      thunkAPI,
      error,
      fallback: 'Failed to create linked site survey service call.',
    });
  }
});

// Delete service ticket
export const deleteServiceTicket = createAsyncThunk<
  ServiceTicketType,
  { id: number; fetchList?: boolean },
  { state: RootReducerState }
>('service-calls/deleteServiceTicket', async ({ id, fetchList = true }, thunkAPI) => {
  try {
    const response = await axios.delete<ServiceTicketType>(`${process.env.REACT_APP_API_URL}/service-ticket/${id}`);
    if (fetchList) {
      refreshServiceTicketsListAndSummary(thunkAPI);
    }
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data.serviceTicket;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting service ticket!' });
  }
});

export const updateServiceTicketDetails = createAsyncThunk<
  ServiceTicketType,
  {
    id: number;
    data: {
      orgId?: number;
      locationId?: number;
      locationName?: string;
      serviceVariant?: string;
      isSiteUnderConstruction?: boolean;
      isCutoverInsideWiringNeeded?: boolean;
      requestedDateTimes?: Array<{
        date: string;
        timeOptionId: number;
      }>;
      installationService?: {
        isNewLine: boolean;
      };
      breakfixService?: {
        description: string;
      };
      customService?: {
        description: string;
      };
      siteSurveyService?: {
        description: string;
      };
      virtualSiteSurveyService?: {
        description: string;
      };
    };
  },
  { state: RootReducerState }
>('service-calls/updateServiceTicketDetails', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<ServiceTicketType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}`,
      data
    );

    thunkAPI.dispatch(getServiceTicket({ id }));

    return response.data.serviceTicket;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating service ticket details!' });
  }
});

// Post comment to service ticket
export const postServiceTicketComment = createAsyncThunk<
  ServiceTicketType,
  {
    data: {
      createdBy: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
      };
      serviceTicketId: number;
      message: string;
      isEmailComment?: boolean;
      emailComment?: string;
      commentChanges?: string[];
      emailMessageId?: number | null;
    };
    isStatusUpdateComment?: boolean;
    isSystemGeneratedComment?: boolean;
  },
  { state: RootReducerState }
>(
  'service-calls/postServiceTicketComment',
  async ({ data, isStatusUpdateComment = false, isSystemGeneratedComment = false }, thunkAPI) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/service-ticket/${data.serviceTicketId}/comment`;

      const response = await axios.post<ServiceTicketType>(url, data);

      thunkAPI.dispatch(getServiceTicket({ id: data.serviceTicketId }));

      // Extract the service ticket from the response and add the comment flags
      return { ...response.data.serviceTicket, isStatusUpdateComment, isSystemGeneratedComment };
    } catch (error) {
      console.error('Error posting service ticket comment:', error);
      console.error('Error details:', (error as AxiosError).response?.data);
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error posting comment!' });
    }
  }
);

export const getServiceTicketNotes = createAsyncThunk<Note[], { id: number }, { state: RootReducerState }>(
  'service-calls/getServiceTicketNotes',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/note`);
      return response.data.notes || [];
    } catch (error) {
      console.error('Error fetching service ticket notes:', error);
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching notes!' });
    }
  }
);

export const postServiceTicketNote = createAsyncThunk<
  { message: string },
  {
    data: {
      createdBy: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
      };
      serviceTicketId: number;
      message: string;
    };
  },
  { state: RootReducerState }
>('service-calls/postServiceTicketNote', async ({ data }, thunkAPI) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/service-ticket/${data.serviceTicketId}/note`;

    const response = await axios.post(url, data);

    thunkAPI.dispatch(getServiceTicket({ id: data.serviceTicketId }));

    return response.data;
  } catch (error) {
    console.error('Error posting service ticket note:', error);
    console.error('Error details:', (error as AxiosError).response?.data);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error posting note!' });
  }
});

export const getServiceTicketActionLogs = createAsyncThunk<ActionLog[], { id: number }, { state: RootReducerState }>(
  'serviceCalls/getServiceTicketActionLogs',
  async ({ id }, thunkAPI) => {
    const url = `${process.env.REACT_APP_API_URL}/service-ticket/${id}/action-logs`;
    try {
      const response = await axios.get(url);
      return response.data.actionLogs;
    } catch (error) {
      console.error('Error fetching action logs:', error);
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching action logs!' });
    }
  }
);

export const postServiceTicketTechnician = createAsyncThunk<
  ServiceTicketType,
  {
    id: number;
    data: {
      technicianId: number;
    };
  },
  { state: RootReducerState }
>('service-calls/postServiceTicketTechnician', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.post<ServiceTicketType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}/technicians`,
      data
    );

    thunkAPI.dispatch(getServiceTicket({ id }));

    return response.data.serviceTicket;
  } catch (error) {
    console.error('Error adding technician to service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error adding technician!' });
  }
});

export const deleteServiceTicketTechnician = createAsyncThunk<
  ServiceTicketType,
  { id: number; technicianId: number },
  { state: RootReducerState }
>('service-calls/deleteServiceTicketTechnician', async ({ id, technicianId }, thunkAPI) => {
  try {
    const response = await axios.delete<ServiceTicketType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}/technicians/${technicianId}`
    );

    thunkAPI.dispatch(getServiceTicket({ id }));

    return response.data.serviceTicket;
  } catch (error) {
    console.error('Error removing technician from service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error removing technician!' });
  }
});

export const updateServiceTicketScheduledDateTime = createAsyncThunk<
  ServiceTicketType,
  {
    id: number;
    data: {
      date: string;
      timeOptionId: number;
    };
    fetchList?: boolean;
  },
  { state: RootReducerState }
>('service-calls/updateServiceTicketScheduledDateTime', async ({ id, data, fetchList = true }, thunkAPI) => {
  try {
    const response = await axios.patch<ServiceTicketType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}/scheduled-datetime`,
      data
    );

    thunkAPI.dispatch(getServiceTicket({ id }));

    if (fetchList) {
      refreshServiceTicketsListAndSummary(thunkAPI);
    }

    return response.data.serviceTicket;
  } catch (error) {
    console.error('Error updating scheduled date and time:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating scheduled date and time!' });
  }
});

export const deleteServiceTicketScheduledDateTime = createAsyncThunk<
  ServiceTicketType,
  {
    id: number;
    fetchList?: boolean;
  },
  { state: RootReducerState }
>('service-calls/deleteServiceTicketScheduledDateTime', async ({ id, fetchList = true }, thunkAPI) => {
  try {
    const response = await axios.delete<ServiceTicketType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}/scheduled-datetime`
    );

    thunkAPI.dispatch(getServiceTicket({ id }));

    if (fetchList) {
      refreshServiceTicketsListAndSummary(thunkAPI);
    }

    return response.data.serviceTicket;
  } catch (error) {
    console.error('Error deleting scheduled date and time:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error deleting scheduled date and time!' });
  }
});

// Add contact to service ticket
export const addServiceTicketContact = createAsyncThunk<
  any,
  {
    id: number;
    contact: {
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: {
        number: string;
        countryCode: string;
      };
      role: string;
    };
  }
>('serviceCalls/addServiceTicketContact', async ({ id, contact }, thunkAPI) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/contact`, contact);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to add contact' });
  }
});

// Update service ticket contact
export const updateServiceTicketContact = createAsyncThunk<
  any,
  {
    id: number;
    contactId: number;
    contact: {
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: {
        number: string;
        countryCode: string;
      };
      role: string;
    };
  }
>('serviceCalls/updateServiceTicketContact', async ({ id, contactId, contact }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}/contact/${contactId}`,
      contact
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to update contact' });
  }
});

// Delete service ticket contact
export const deleteServiceTicketContact = createAsyncThunk<void, { id: number; contactId: number }>(
  'serviceCalls/deleteServiceTicketContact',
  async ({ id, contactId }, thunkAPI) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/contact/${contactId}`);
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to delete contact' });
    }
  }
);

// Add CC user to service ticket
export const addServiceTicketCCUser = createAsyncThunk<
  any,
  {
    id: number;
    userId: number;
  },
  { state: RootReducerState }
>('service-calls/addServiceTicketCCUser', async ({ id, userId }, thunkAPI) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/cc-notify`, {
      userId: userId,
    });

    thunkAPI.dispatch(getServiceTicket({ id }));

    return response.data;
  } catch (error) {
    console.error('Error adding CC user:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error adding CC user!' });
  }
});

// Remove CC user from service ticket
export const removeServiceTicketCCUser = createAsyncThunk<
  void,
  { id: number; userId: number },
  { state: RootReducerState }
>('service-calls/removeServiceTicketCCUser', async ({ id, userId }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/cc-notify/${userId}`);

    // Refresh service ticket details to get updated CC users
    thunkAPI.dispatch(getServiceTicket({ id }));

    return;
  } catch (error) {
    console.error('Error removing CC user:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error removing CC user!' });
  }
});

const getFileTypeFromMimeType = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (
    mimeType === 'application/pdf' ||
    mimeType.includes('document') ||
    mimeType.includes('msword') ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('presentation')
  )
    return 'document';
  return 'other';
};

export const postServiceTicketAttachment = createAsyncThunk<
  void,
  {
    id: number;
    file: File;
    title?: string;
    description?: string;
    fileType?: string;
  },
  { state: RootReducerState }
>('service-calls/postServiceTicketAttachment', async ({ id, file, title, description, fileType }, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    formData.append('fileType', fileType || getFileTypeFromMimeType(file.type));

    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/attachment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    thunkAPI.dispatch(getServiceTicket({ id }));

    return response.data;
  } catch (error) {
    console.error('Error adding attachment to service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error adding attachment!' });
  }
});

export const postServiceTicketAttachments = createAsyncThunk<
  void,
  {
    id: number;
    files: File[];
    metadata?: Array<{ title?: string; description?: string; fileType?: string }>;
  },
  { state: RootReducerState }
>('service-calls/postServiceTicketAttachments', async ({ id, files, metadata }, thunkAPI) => {
  try {
    const uploadPromises = files.map((file, index) => {
      const formData = new FormData();
      formData.append('file', file);
      const meta = metadata?.[index];
      if (meta?.title) formData.append('title', meta.title);
      if (meta?.description) formData.append('description', meta.description);
      formData.append('fileType', meta?.fileType || getFileTypeFromMimeType(file.type));

      return axios.post(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/attachment`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    });

    await Promise.all(uploadPromises);
    thunkAPI.dispatch(getServiceTicket({ id }));

    return;
  } catch (error: any) {
    console.error('Error adding attachments to service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error adding attachments!' });
  }
});

export const deleteServiceTicketAttachment = createAsyncThunk<
  void,
  {
    serviceTicketId: number;
    attachmentId: number;
  },
  { state: RootReducerState }
>('service-calls/deleteServiceTicketAttachment', async ({ serviceTicketId, attachmentId }, thunkAPI) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/attachment/${attachmentId}`
    );

    thunkAPI.dispatch(getServiceTicket({ id: serviceTicketId }));

    return response.data;
  } catch (error) {
    console.error('Error deleting attachment from service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error deleting attachment!' });
  }
});

export const downloadServiceTicketAttachment = createAsyncThunk<
  string,
  {
    serviceTicketId: number;
    attachmentId: number;
    filename?: string;
  },
  { state: RootReducerState }
>('service-calls/downloadServiceTicketAttachment', async ({ serviceTicketId, attachmentId, filename }, thunkAPI) => {
  try {
    const response = await axios.get<{ downloadUrl: string }>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/attachment/${attachmentId}/download?disposition=attachment`
    );

    const downloadUrl = response.data.downloadUrl;

    const link = document.createElement('a');
    link.href = downloadUrl;
    if (filename) {
      link.download = filename;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return downloadUrl;
  } catch (error) {
    console.error('Error downloading attachment from service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error downloading attachment!' });
  }
});

export const getServiceTicketAttachmentDownloadUrl = createAsyncThunk<
  string,
  {
    serviceTicketId: number;
    attachmentId: number;
  },
  { state: RootReducerState }
>('service-calls/getServiceTicketAttachmentDownloadUrl', async ({ serviceTicketId, attachmentId }, thunkAPI) => {
  try {
    const response = await axios.get<{ downloadUrl: string }>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/attachment/${attachmentId}/download?disposition=inline`
    );

    return response.data.downloadUrl;
  } catch (error) {
    console.error('Error fetching attachment download URL from service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error loading attachment preview!' });
  }
});

export const getTechnicians = createAsyncThunk<
  PaginationType<TechnicianType>,
  {
    page?: number;
    limit?: number;
    order?: string;
    q?: string;
    serviceOrganizationIds?: number | string | number[];
    rehirable?: boolean | string;
    serviceZipCodes?: string | string[];
  },
  { state: RootReducerState }
>('service-calls/getTechnicians', async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 25, order = '-id', q = '', serviceOrganizationIds, rehirable, serviceZipCodes } = params;

    const parsedServiceOrganizationIds = Array.isArray(serviceOrganizationIds)
      ? serviceOrganizationIds.map(id => Number(id)).filter(id => !Number.isNaN(id) && id > 0)
      : typeof serviceOrganizationIds === 'string' && serviceOrganizationIds.trim()
      ? serviceOrganizationIds
          .split(',')
          .map(id => Number(id.trim()))
          .filter(id => !Number.isNaN(id) && id > 0)
      : serviceOrganizationIds !== undefined && serviceOrganizationIds !== null && serviceOrganizationIds !== ''
      ? [Number(serviceOrganizationIds)].filter(id => !Number.isNaN(id) && id > 0)
      : [];

    const parsedRehirable =
      typeof rehirable === 'boolean' ? rehirable : rehirable === 'yes' ? true : rehirable === 'no' ? false : undefined;

    const parsedServiceZipCodes = Array.isArray(serviceZipCodes)
      ? serviceZipCodes.map(zip => String(zip).trim()).filter(Boolean)
      : typeof serviceZipCodes === 'string' && serviceZipCodes.trim()
      ? serviceZipCodes
          .split(',')
          .map(zip => zip.trim())
          .filter(Boolean)
      : [];

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      order,
      ...(q && { q }),
      ...(parsedServiceOrganizationIds.length > 0 && {
        serviceOrganizationIds: parsedServiceOrganizationIds.join(','),
      }),
      ...(typeof parsedRehirable === 'boolean' && { rehirable: String(parsedRehirable) }),
      ...(parsedServiceZipCodes.length > 0 && { serviceZipCodes: parsedServiceZipCodes.join(',') }),
    });

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/service-ticket/technician?${queryParams.toString()}`
    );

    return {
      items: response.data.technicians || [],
      meta: response.data.meta || { totalItems: 0, totalPages: 0 },
    };
  } catch (error) {
    console.error('Error fetching technicians:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching technicians!' });
  }
});

export const getTechnicianServiceZipCodes = createAsyncThunk<string[], void, { state: RootReducerState }>(
  'service-calls/getTechnicianServiceZipCodes',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/service-ticket/technician/service-zip-codes`);
      return Array.isArray(response.data?.serviceZipCodes) ? response.data.serviceZipCodes : [];
    } catch (error) {
      console.error('Error fetching technician service zip codes:', error);
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching zip codes!' });
    }
  }
);

export const addTechnician = createAsyncThunk<
  { message: string },
  {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: {
      number: string;
      countryCode: string;
    };
    serviceZipCodes?: string[];
    serviceOrganizationIds?: number[];
    isRehirable?: boolean;
    notes?: string;
  },
  { state: RootReducerState }
>('service-calls/addTechnician', async (data, thunkAPI) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-ticket/technician`, data);

    thunkAPI.dispatch(getTechnicians({}));

    return response.data;
  } catch (error) {
    console.error('Error adding technician:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error adding technician!' });
  }
});

export const removeTechnician = createAsyncThunk<
  { message: string },
  { technicianId: number },
  { state: RootReducerState }
>('service-calls/removeTechnician', async ({ technicianId }, thunkAPI) => {
  try {
    const response = await axios.delete(`${process.env.REACT_APP_API_URL}/service-ticket/technician/${technicianId}`);

    thunkAPI.dispatch(getTechnicians({}));

    return response.data;
  } catch (error: any) {
    console.error('Error removing technician:', error);
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Error removing technician!');
  }
});

export const updateTechnician = createAsyncThunk<
  { message: string },
  {
    technicianId: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: {
      number: string;
      countryCode: string;
    };
    serviceZipCodes?: string[];
    serviceOrganizationIds?: number[];
    isRehirable?: boolean;
    notes?: string;
  },
  { state: RootReducerState }
>('service-calls/updateTechnician', async ({ technicianId, ...data }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/service-ticket/technician/${technicianId}`,
      data
    );

    thunkAPI.dispatch(getTechnicians({}));

    return response.data;
  } catch (error) {
    console.error('Error updating technician:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating technician!' });
  }
});

// Service Organization Actions
export const getServiceOrganizations = createAsyncThunk<
  PaginationType<ServiceOrganizationListItemType>,
  {
    page?: number;
    limit?: number;
    order?: string;
    q?: string;
  },
  { state: RootReducerState }
>('service-calls/getServiceOrganizations', async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 1000, order = '-id', q = '' } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      order,
      ...(q && { q }),
    });

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/service-ticket/service-organization?${queryParams.toString()}`
    );

    return {
      items: response.data.serviceOrganizations || [],
      meta: response.data.meta || { totalItems: 0, totalPages: 0 },
    };
  } catch (error) {
    console.error('Error fetching service organizations:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching service organizations!' });
  }
});

export const addServiceOrganization = createAsyncThunk<
  { message: string },
  {
    name: string;
    system?: ServiceOrganizationSystemType;
  },
  { state: RootReducerState }
>('service-calls/addServiceOrganization', async (data, thunkAPI) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-ticket/service-organization`, data);

    thunkAPI.dispatch(getServiceOrganizations({}));

    return response.data;
  } catch (error) {
    console.error('Error adding service organization:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error adding service organization!' });
  }
});

export const removeServiceOrganization = createAsyncThunk<
  { message: string },
  { serviceOrganizationId: number },
  { state: RootReducerState }
>('service-calls/removeServiceOrganization', async ({ serviceOrganizationId }, thunkAPI) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/service-ticket/service-organization/${serviceOrganizationId}`
    );

    thunkAPI.dispatch(getServiceOrganizations({}));

    return response.data;
  } catch (error) {
    console.error('Error removing service organization:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error removing service organization!' });
  }
});

export const updateServiceOrganization = createAsyncThunk<
  { message: string },
  {
    serviceOrganizationId: number;
    name: string;
    system?: ServiceOrganizationSystemType | null;
  },
  { state: RootReducerState }
>('service-calls/updateServiceOrganization', async ({ serviceOrganizationId, ...data }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/service-ticket/service-organization/${serviceOrganizationId}`,
      data
    );

    thunkAPI.dispatch(getServiceOrganizations({}));

    return response.data;
  } catch (error) {
    console.error('Error updating service organization:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating service organization!' });
  }
});

export const changeServiceTicketServiceOrganization = createAsyncThunk<
  ServiceTicketType,
  {
    serviceTicketId: number;
    serviceOrganizationId: number | null;
  },
  { state: RootReducerState }
>(
  'service-calls/changeServiceTicketServiceOrganization',
  async ({ serviceTicketId, serviceOrganizationId }, thunkAPI) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/service-organization`,
        { serviceOrganizationId }
      );

      thunkAPI.dispatch(getServiceTicket({ id: serviceTicketId }));

      return response.data.serviceTicket;
    } catch (error: any) {
      console.error('Error changing service ticket service organization:', error);
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Error changing service organization!');
    }
  }
);

export const cancelServiceTicket = createAsyncThunk<
  ServiceTicketType,
  { id: number; fetchList?: boolean },
  { state: RootReducerState }
>('service-calls/cancelServiceTicket', async ({ id, fetchList = true }, thunkAPI) => {
  try {
    const response = await axios.patch<ServiceTicketType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}/cancel`
    );

    if (fetchList) {
      refreshServiceTicketsListAndSummary(thunkAPI);
    }

    thunkAPI.dispatch(getServiceTicket({ id }));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    console.error('Error canceling service ticket:', error);
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error canceling service ticket!' });
  }
});

// Add assigned users (internal telcloud users only) to service ticket
export const addServiceTicketAssignedUser = createAsyncThunk<
  ServiceTicketType,
  {
    id: number;
    userId: number;
  },
  { state: RootReducerState }
>('service-calls/addServiceTicketAssignedUser', async ({ id, userId }, thunkAPI) => {
  try {
    const response = await axios.post<ServiceTicketType>(
      `${process.env.REACT_APP_API_URL}/service-ticket/${id}/assign-user`,
      {
        userId: userId,
      }
    );
    thunkAPI.dispatch(getServiceTicket({ id }));

    return response.data;
  } catch (error) {
    console.error('Error adding assigned user:', error);
    const axiosError = error as { response?: { data?: { message?: string } } };
    return thunkAPI.rejectWithValue(axiosError.response?.data?.message || 'Error adding assigned user!');
  }
});

// Remove assigned user from service ticket
export const removeServiceTicketAssignedUser = createAsyncThunk<
  void,
  { id: number; userId: number },
  { state: RootReducerState }
>('service-calls/removeServiceTicketAssignedUser', async ({ id, userId }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/service-ticket/${id}/assign-user/${userId}`);
    thunkAPI.dispatch(getServiceTicket({ id }));
    return;
  } catch (error) {
    console.error('Error removing assigned user:', error);
    const axiosError = error as { response?: { data?: { message?: string } } };
    return thunkAPI.rejectWithValue(axiosError.response?.data?.message || 'Error removing assigned user!');
  }
});

export const getChecklistTemplates = createAsyncThunk<
  ChecklistTemplateType[],
  { serviceVariant?: string; controller?: AbortController; page?: number; limit?: number; order?: string; q?: string },
  { state: RootReducerState }
>('service-calls/getChecklistTemplates', async (args = {}, thunkAPI) => {
  try {
    const serviceVariant =
      typeof args.serviceVariant === 'string' && args.serviceVariant.trim() !== '' ? args.serviceVariant : undefined;
    const query = serviceVariant ? `?serviceVariant=${encodeURIComponent(serviceVariant)}` : '';
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/service-ticket/checklist-template${query}`, {
      signal: args.controller?.signal,
    });
    return response.data || [];
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching checklist templates!' });
  }
});

export const getChecklistTemplate = createAsyncThunk<
  ChecklistTemplateType,
  { templateId: number },
  { state: RootReducerState }
>('service-calls/getChecklistTemplate', async ({ templateId }, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/service-ticket/checklist-template/${templateId}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching checklist template details!' });
  }
});

export const createChecklistTemplate = createAsyncThunk<
  ChecklistTemplateType,
  {
    name: string;
    serviceVariant: string;
    isDefault?: boolean;
    isActive?: boolean;
    items: Array<{
      label: string;
      itemType: 'checkbox' | 'yes_no';
      isRequired: boolean;
      sortOrder: number;
      automationRule?: string;
    }>;
  },
  { state: RootReducerState }
>('service-calls/createChecklistTemplate', async (payload, thunkAPI) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/service-ticket/checklist-template`, {
      ...payload,
      items: Array.isArray(payload.items) ? payload.items : [],
    });
    const { checklistTemplatesRouteParams } = thunkAPI.getState().serviceCallsReducer;
    thunkAPI.dispatch(
      getChecklistTemplates({
        serviceVariant: checklistTemplatesRouteParams.serviceVariant?.trim() || undefined,
      })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error creating checklist template!' });
  }
});

export const updateChecklistTemplate = createAsyncThunk<
  ChecklistTemplateType,
  {
    templateId: number;
    data: {
      name?: string;
      serviceVariant?: string;
      isDefault?: boolean;
      isActive?: boolean;
      items?: Array<{
        label: string;
        itemType: 'checkbox' | 'yes_no';
        isRequired: boolean;
        sortOrder: number;
        automationRule?: string;
      }>;
    };
  },
  { state: RootReducerState }
>('service-calls/updateChecklistTemplate', async ({ templateId, data }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/service-ticket/checklist-template/${templateId}`,
      {
        ...data,
        ...(data.items ? { items: data.items } : {}),
      }
    );
    const { checklistTemplatesRouteParams } = thunkAPI.getState().serviceCallsReducer;
    thunkAPI.dispatch(
      getChecklistTemplates({
        serviceVariant: checklistTemplatesRouteParams.serviceVariant?.trim() || undefined,
      })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating checklist template!' });
  }
});

export const deleteChecklistTemplate = createAsyncThunk<void, { templateId: number }, { state: RootReducerState }>(
  'service-calls/deleteChecklistTemplate',
  async ({ templateId }, thunkAPI) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/service-ticket/checklist-template/${templateId}`);
      const { checklistTemplatesRouteParams } = thunkAPI.getState().serviceCallsReducer;
      thunkAPI.dispatch(
        getChecklistTemplates({
          serviceVariant: checklistTemplatesRouteParams.serviceVariant?.trim() || undefined,
        })
      );
      return;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error deleting checklist template!' });
    }
  }
);

export const setDefaultChecklistTemplate = createAsyncThunk<
  ChecklistTemplateType,
  { templateId: number },
  { state: RootReducerState }
>('service-calls/setDefaultChecklistTemplate', async ({ templateId }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/service-ticket/checklist-template/${templateId}/default`
    );
    const { checklistTemplatesRouteParams } = thunkAPI.getState().serviceCallsReducer;
    thunkAPI.dispatch(
      getChecklistTemplates({
        serviceVariant: checklistTemplatesRouteParams.serviceVariant?.trim() || undefined,
      })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error setting default checklist template!' });
  }
});

export const getServiceTicketChecklist = createAsyncThunk<
  ServiceTicketChecklistType,
  { serviceTicketId: number },
  { state: RootReducerState }
>('service-calls/getServiceTicketChecklist', async ({ serviceTicketId }, thunkAPI) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/checklist`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching service ticket checklist!' });
  }
});

export const updateServiceTicketChecklistItem = createAsyncThunk<
  ServiceTicketChecklistType,
  { serviceTicketId: number; itemId: number; checked: boolean; value?: string; notes?: string },
  { state: RootReducerState }
>('service-calls/updateServiceTicketChecklistItem', async ({ serviceTicketId, itemId, ...payload }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/checklist/item/${itemId}`,
      payload
    );
    thunkAPI.dispatch(getServiceTicketChecklist({ serviceTicketId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating checklist item!' });
  }
});

export const completeServiceTicketChecklist = createAsyncThunk<
  ServiceTicketChecklistType,
  { serviceTicketId: number },
  { state: RootReducerState }
>('service-calls/completeServiceTicketChecklist', async ({ serviceTicketId }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/service-ticket/${serviceTicketId}/checklist/complete`
    );
    thunkAPI.dispatch(getServiceTicketChecklist({ serviceTicketId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error completing checklist!' });
  }
});
